import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { X, Save } from 'lucide-react';

import {
  createResponsePartnerService,
  updateResponsePartnerService,
} from '../../utils/responsePartnerService';
import { getTimeZonesWithOffsets } from '../../utils/dateTimeUtils';
import {
  upsertPartner,
  setError as setReducerError,
} from '../../reducers/responsePartnerReducer';
import {
  DEFAULT_CHANNELS,
  parseEmailsInput,
  parseStringList,
  formatEmails,
  formatStringList,
  slugify,
} from '../../utils/responsePartnerUtils';

const buildInitialForm = (partner) => ({
  name: partner?.name ?? '',
  slug: partner?.slug ?? '',
  description: partner?.description ?? '',
  serviceLines: formatStringList(partner?.serviceLines),
  defaultDispatchEmails: formatEmails(partner?.defaultDispatchEmails),
  defaultChannel: partner?.defaultChannel ?? 'email',
  afterHoursDispatchEmails: formatEmails(partner?.afterHoursDispatchEmails),
  afterHoursStartTime: partner?.afterHoursStartTime ?? '',
  afterHoursEndTime: partner?.afterHoursEndTime ?? '',
  dispatchTimezone: partner?.dispatchTimezone ?? '',
  ownerScope: partner?.ownerUserId ? 'user' : 'global',
  ownerUserId: partner?.ownerUserId ?? '',
  active: partner?.active !== false,
});

const ResponsePartnerForm = ({ partner, onClose }) => {
  const dispatch = useDispatch();
  const isEditing = Boolean(partner?.id);
  const [form, setForm] = useState(() => buildInitialForm(partner));
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const slugPreview = useMemo(() => slugify(form.slug || form.name), [form.slug, form.name]);

  const updateField = (field) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const formErrors = {};
    if (!form.name.trim()) {
      formErrors.name = 'Name is required';
    }
    if (!form.slug.trim()) {
      formErrors.slug = 'Slug is required';
    }
    if (!form.description.trim()) {
      formErrors.description = 'Description is required';
    }
    if (!form.serviceLines.trim()) {
      formErrors.serviceLines = 'Service lines are required';
    }
    if (!form.defaultDispatchEmails.trim()) {
      formErrors.defaultDispatchEmails = 'Default dispatch emails are required';
    }
    if (!form.dispatchTimezone.trim()) {
      formErrors.dispatchTimezone = 'Dispatch timezone is required';
    }
    if (!form.defaultChannel.trim()) {
      formErrors.defaultChannel = 'Default channel is required';
    }
    if (!form.ownerScope.trim()) {
      formErrors.ownerScope = 'Owner scope is required';
    }
    if (form.ownerScope === 'user' && !form.ownerUserId.trim()) {
      formErrors.ownerUserId = 'Owner user id is required';
    }
    if (!form.active) {
      formErrors.active = 'Active is required';
    }
    if (Object.keys(formErrors).length > 0) {
      setFormErrors({ ...formErrors });
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormErrors({});

    if (!validateForm()) {
      return;
    }

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || form.name.trim(),
      description: form.description.trim() || null,
      serviceLines: parseStringList(form.serviceLines),
      defaultDispatchEmails: parseEmailsInput(form.defaultDispatchEmails),
      defaultChannel: form.defaultChannel,
      afterHoursDispatchEmails: parseEmailsInput(form.afterHoursDispatchEmails),
      afterHoursStartTime: form.afterHoursStartTime || null,
      afterHoursEndTime: form.afterHoursEndTime || null,
      dispatchTimezone: form.dispatchTimezone.trim() || null,
      active: Boolean(form.active),
    };

    if (form.ownerScope === 'user') {
      const trimmed = String(form.ownerUserId || '').trim();
      if (!trimmed) {
        setFormErrors({ ownerUserId: 'User-scoped partners require an owner user id' });
        return;
      }
      payload.ownerUserId = trimmed;
    } else {
      payload.ownerUserId = null;
    }

    setSubmitting(true);
    try {
      const saved = isEditing
        ? await updateResponsePartnerService(partner.id, payload)
        : await createResponsePartnerService(payload);
      dispatch(upsertPartner(saved));
      onClose?.();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save partner';
      setFormErrors({ message: message });
      dispatch(setReducerError(message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-lg w-[640px] max-w-full max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold m-0">
            {isEditing ? 'Edit Response Partner' : 'New Response Partner'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-amp-track-section="Response Partner Form"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Response Partner Form"
            className="bg-transparent text-stone-600 hover:text-stone-900 p-1 m-0"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto px-4 py-3 flex flex-col gap-3 text-sm">
          {Object.keys(formErrors).length > 0 && (
            <div className="p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs">
              {(() => {
                return Object.values(formErrors).map((error) => (
                  <div key={error}>{error}</div>
                ));
              })()}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Name *</span>
              <input
                type="text"
                value={form.name}
                onChange={updateField('name')}
                required
                className="border border-stone-300 rounded-md px-2 py-1.5"
                placeholder="e.g. Insurcomm Boston"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Slug</span>
              <input
                type="text"
                value={form.slug}
                onChange={updateField('slug')}
                className="border border-stone-300 rounded-md px-2 py-1.5 font-mono"
                placeholder="auto-from-name"
              />
              <span className="text-xs text-stone-500">
                Stored as: <span className="font-mono">{slugPreview || '—'}</span>
              </span>
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="font-semibold text-stone-700">Description</span>
            <textarea
              value={form.description}
              onChange={updateField('description')}
              rows={2}
              className="border border-stone-300 rounded-md px-2 py-1.5"
              placeholder="Short summary of when this partner is used"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-semibold text-stone-700">Service lines</span>
            <input
              type="text"
              value={form.serviceLines}
              onChange={updateField('serviceLines')}
              className="border border-stone-300 rounded-md px-2 py-1.5"
              placeholder="HVAC, plumbing, water restoration (comma-separated)"
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Default dispatch emails</span>
              <textarea
                value={form.defaultDispatchEmails}
                onChange={updateField('defaultDispatchEmails')}
                rows={2}
                className="border border-stone-300 rounded-md px-2 py-1.5"
                placeholder="ops@example.com, dispatch@example.com"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">After-hours dispatch emails</span>
              <textarea
                value={form.afterHoursDispatchEmails}
                onChange={updateField('afterHoursDispatchEmails')}
                rows={2}
                className="border border-stone-300 rounded-md px-2 py-1.5"
                placeholder="oncall@example.com"
              />
            </label>
          </div>
          
          <div className="flex flex-col gap-1">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Dispatch timezone</span>
              <select
                value={form.dispatchTimezone}
                onChange={updateField('dispatchTimezone')}
                className="border border-stone-300 rounded-md px-2 py-1.5 bg-white"
              >
                <option value="">Select timezone...</option>
                {getTimeZonesWithOffsets().map((tz) => (
                  <option key={tz.label} value={tz.label}>
                    {tz.offset} - {tz.label}
                  </option>
                ))}
              </select>
         
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">After-hours start</span>
              <input
                type="time"
                value={form.afterHoursStartTime || ''}
                onChange={updateField('afterHoursStartTime')}
                className="border border-stone-300 rounded-md px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">After-hours end</span>
              <input
                type="time"
                value={form.afterHoursEndTime || ''}
                onChange={updateField('afterHoursEndTime')}
                className="border border-stone-300 rounded-md px-2 py-1.5"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Default channel</span>
              <select
                value={form.defaultChannel}
                onChange={updateField('defaultChannel')}
                className="border border-stone-300 rounded-md px-2 py-1.5 bg-white"
              >
                {DEFAULT_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Scope</span>
              <select
                value={form.ownerScope}
                onChange={updateField('ownerScope')}
                className="border border-stone-300 rounded-md px-2 py-1.5 bg-white"
              >
                <option value="global">Global</option>
                <option value="user">User-scoped</option>
              </select>
            </label>

            {form.ownerScope === 'user' ? (
              <label className="flex flex-col gap-1">
                <span className="font-semibold text-stone-700">Owner user id</span>
                <input
                  type="text"
                  value={form.ownerUserId}
                  onChange={updateField('ownerUserId')}
                  className="border border-stone-300 rounded-md px-2 py-1.5 font-mono"
                  placeholder="users.id"
                />
              </label>
            ) : (
              <div className="flex items-center text-xs text-stone-500 md:pt-6">
                Global partners are visible to all admins.
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-stone-700">
            <input
              type="checkbox"
              checked={Boolean(form.active)}
              onChange={updateField('active')}
            />
            <span className="font-semibold">Active</span>
          </label>
        </div>

        <div className="flex justify-end items-center gap-2 border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-amp-track-section="Response Partner Form"
            data-amp-track-action="Cancel_Click"
            data-amp-track-description="Cancel Response Partner Form"
            className="flex items-center gap-2 bg-transparent text-stone-700 hover:bg-stone-200 rounded-md px-3 py-2 disabled:opacity-50"
          >
            <X size={18} /> Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-amp-track-section="Response Partner Form"
            data-amp-track-action="Save_Click"
            data-amp-track-description="Save Response Partner Form"
            className="flex items-center gap-2 bg-sky-600 text-white hover:bg-sky-500 rounded-md px-3 py-2 disabled:opacity-50"
          >
            <Save size={18} /> {submitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Partner'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResponsePartnerForm;
